/**
 * Loading / Error / Empty state theo quy chuẩn N2NAI (mục 5.3).
 * Dùng khi trang load dữ liệu từ RTK Query để luôn có trạng thái rõ ràng.
 */
import { Box, CircularProgress, Alert, Typography, Button } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'

interface PageDataStateProps {
  /** Đang tải (isLoading / isFetching) */
  isLoading?: boolean
  /** Lỗi từ RTK Query hoặc message string */
  error?: unknown
  /** Không có dữ liệu (sau khi load xong, list rỗng) */
  empty?: boolean
  /** Message khi empty (dùng khi không có emptyContent) */
  emptyMessage?: string
  /** UI tùy chỉnh khi empty (ưu tiên hơn emptyMessage) */
  emptyContent?: React.ReactNode
  /** Callback retry khi error */
  onRetry?: () => void
  /** Nội dung khi có dữ liệu */
  children: React.ReactNode
  /** Chiều cao tối thiểu vùng loading (px) */
  minHeight?: number
}

export function PageDataState({
  isLoading,
  error,
  empty,
  emptyMessage = 'Chưa có dữ liệu.',
  emptyContent,
  onRetry,
  children,
  minHeight = 280,
}: PageDataStateProps) {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 6,
          minHeight,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    const message = typeof error === 'object' && error !== null && 'data' in error
      ? String((error as { data?: { detail?: string } }).data?.detail ?? 'Không tải được dữ liệu.')
      : typeof error === 'string'
        ? error
        : 'Không tải được dữ liệu. Thử lại sau.'
    return (
      <Box sx={{ py: 3 }}>
        <Alert
          severity="error"
          action={
            onRetry ? (
              <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={onRetry}>
                Thử lại
              </Button>
            ) : undefined
          }
        >
          {message}
        </Alert>
      </Box>
    )
  }

  if (empty) {
    if (emptyContent) return <>{emptyContent}</>
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          minHeight,
        }}
      >
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    )
  }

  return <>{children}</>
}
